/**
 * StudioSai — apparition au défilement, réversible.
 *
 * Deux mécanismes, choisis selon le navigateur :
 *
 * 1. `animation-timeline: view()` quand il est disponible (Chrome, Edge).
 *    L'animation devient une fonction de la position de l'élément dans la
 *    fenêtre : elle se joue en descendant, se rejoue à l'envers en
 *    remontant, sans le moindre calcul JavaScript. C'est le seul moyen
 *    d'être réellement synchronisé au défilement.
 *
 * 2. Sinon, un IntersectionObserver qui garde les éléments sous
 *    surveillance — sans `unobserve` — pour pouvoir masquer à la sortie
 *    et révéler de nouveau à chaque retour.
 *
 * Sécurité : le CSS ne masque que sous la classe `sai-motion`, posée
 * ici. Sans ce script, rien n'est masqué.
 */
(function () {
  var root = document.documentElement;

  // Shopify's editor continuously changes the preview viewport and reloads
  // sections; scroll timelines can leave essential copy in an intermediate
  // blurred state there.
  if (root.classList.contains('shopify-design-mode')) return;

  // Product copy and purchase details must stay legible at every scroll
  // position. The product layout also contains deferred footer sections,
  // whose geometry is not stable enough for scroll-linked reveals.
  if (document.querySelector('main .product-information[data-sai-product-page]')) return;

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced) return;

  var scrollDriven =
    window.CSS && CSS.supports && CSS.supports('animation-timeline', 'view()');

  if (!scrollDriven && !('IntersectionObserver' in window)) return;

  // Blocs de contenu, pas chaque nœud : animer littéralement tout
  // produirait un scintillement permanent et ferait chuter la fluidité.
  var SELECTORS = [
    'main .section > *',
    'main .section-content-wrapper > *',
    'main .product-card',
    'main .collection-card',
    'main .blog-card',
    'main .sb-card',
    'main .sc-card',
    'main .nw-card',
    // Vos sections sur mesure n'utilisent pas la classe `.section` du
    // thème : leurs enfants passaient tous à travers le filet. C'est
    // pourquoi la section des avis clients n'apparaissait jamais.
    'main [class$="__inner"] > *',
    'main [class$="-inner"] > *',
    'main [class$="__header"] > *',
    'main [class$="-card"]',
    'main [class*="__card"]',
    'main .sai-card',
    'main h1, main h2, main h3, main h4',
    'main p',
    'main .button',
    'main img',
    'footer .section > *'
  ].join(',');

  var SKIP = /sai-edge|sai-cluster|sai-sticker|section-background|slideshow-control|marquee|mq-/;

  // Une carte est une unité : elle arrive d'un bloc, jamais découpée en
  // texte, image et badge qui apparaîtraient séparément.
  var CARDS = '.sai-card, .product-card, .collection-card, .blog-card, .sb-card, .sc-card, .nw-card';

  // Candidats plausibles à une position fixe. Filtre grossier mais peu
  // coûteux : on ne paie `getComputedStyle` que sur ces éléments-là.
  var MAYBE_FIXED = '[class*="sticky"], [class*="fixed"], [class*="floating"], dialog, [class*="drawer"], [class*="modal"]';

  /**
   * Un élément animé par `transform` ou `filter` devient bloc conteneur
   * pour ses descendants en `position: fixed` : ceux-ci s'ancrent alors
   * sur lui au lieu de la fenêtre. C'est ce qui décrochait la barre
   * d'ajout au panier, et plus tôt les méga-menus.
   */
  function containsFixed(el) {
    var candidats;
    try {
      candidats = el.querySelectorAll(MAYBE_FIXED);
    } catch (e) {
      return false;
    }
    for (var i = 0; i < candidats.length; i++) {
      if (getComputedStyle(candidats[i]).position === 'fixed') return true;
    }
    return false;
  }

  function eligible(el) {
    if (el.hasAttribute('data-sai-reveal')) return false;
    if (SKIP.test(String(el.className))) return false;
    if (el.closest('.sai-edge, .sai-cluster, .header-section')) return false;

    // Déjà pris en charge par l'entrée de page : deux animations sur le
    // même élément se disputeraient l'opacité et le laisseraient terne.
    if (el.hasAttribute('data-sai-enter') || el.closest('[data-sai-enter]')) return false;

    // Contient un élément fixe : l'animer le décrocherait de la fenêtre.
    if (containsFixed(el)) return false;

    // Contenu interne d'une carte : c'est la carte entière qui s'anime.
    if (!el.matches(CARDS) && el.closest(CARDS)) return false;

    // Un `transform` casse les éléments collants ou fixes : on les laisse.
    var pos = getComputedStyle(el).position;
    if (pos === 'sticky' || pos === 'fixed') return false;

    // Sur une plage `cover`, un élément très haut met une distance de
    // défilement énorme à traverser la fenêtre : il reste presque
    // toujours dans la zone nette du milieu et son animation devient
    // imperceptible. C'est ce qui figeait le carrousel d'avis.
    //
    // Mais on ne l'écarte que s'il a des enfants animables à qui céder
    // la place : sans cette condition, une grande carte d'avis — haute
    // sans rien contenir d'animable — se trouvait écartée elle aussi,
    // et la zone des commentaires restait figée.
    // Un conteneur ample qui renferme plusieurs éléments animables cède
    // la place à ses enfants : ce sont eux qu'on veut voir arriver en
    // cascade. Animé d'un bloc, le carrousel d'avis paraissait figé
    // pendant que son titre, lui, s'animait juste au-dessus.
    //
    // La condition de hauteur est indispensable : sans elle, un petit
    // en-tête de deux lignes céderait aussi, et on finirait par animer
    // chaque mot. Un conteneur haut mais sans enfant animable — une
    // grande carte d'avis, par exemple — s'anime bien lui-même.
    // Un conteneur qui renferme plusieurs cartes cède toujours la place :
    // ce sont les cartes qu'on veut voir arriver en cascade, jamais le
    // bloc entier. Sans condition de hauteur — le carrousel d'avis est
    // court sur mobile tout en contenant douze cartes, et il passait
    // sous le seuil, bloquant tout son contenu.
    if (!el.matches(CARDS) && el.querySelectorAll(CARDS).length >= 2) return false;

    // Même logique pour les conteneurs génériques, mais réservée aux
    // grands blocs : sinon un en-tête de deux lignes céderait aussi, et
    // on finirait par animer chaque mot.
    var box = el.getBoundingClientRect();
    if (!el.matches(CARDS) && box.height > window.innerHeight * 0.45) {
      var kids = [];
      try {
        kids = el.querySelectorAll(SELECTORS);
      } catch (e) {}
      if (kids.length >= 2) return false;
    }

    // Élément imbriqué dans un autre déjà animé : une seule animation
    // suffit, sinon les opacités se multiplient et le texte devient gris.
    if (el.parentElement && el.parentElement.closest('[data-sai-reveal]')) return false;

    return true;
  }

  var observer = null;

  if (!scrollDriven) {
    observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var e = entries[i];
          // Pas de `unobserve` : l'élément doit pouvoir se masquer en
          // sortant et se révéler de nouveau au retour, dans les deux sens.
          e.target.classList.toggle('is-revealed', e.isIntersecting);
        }
      },
      { rootMargin: '-6% 0px -6% 0px', threshold: 0 }
    );
  }

  function arm() {
    var nodes;
    try {
      nodes = document.querySelectorAll(SELECTORS);
    } catch (err) {
      return;
    }

    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (!eligible(el)) continue;

      // Les éléments du dernier écran de la page ne peuvent pas achever
      // une animation en plage `cover` : celle-ci mesure la traversée
      // complète de la fenêtre, or le défilement s'arrête avant. La
      // ligne des moyens de paiement, haute de 30 px sur un écran de
      // 812, ne parcourt que 3,6 % de sa course — elle resterait floue
      // pour toujours. On les bascule sur une plage d'entrée simple.
      var docHeight = Math.max(
        document.documentElement.scrollHeight,
        document.body ? document.body.scrollHeight : 0
      );
      var bottom = el.getBoundingClientRect().bottom + (window.scrollY || 0);
      var nearEnd = docHeight - bottom < window.innerHeight;

      el.setAttribute("data-sai-reveal", nearEnd ? "near-end" : "");

      // Rang de l.element dans sa rangee : la cascade suit la grille,
      // pas l.ordre brut du document, sinon le decalage parait aleatoire.
      var siblings = el.parentElement ? el.parentElement.children : [];
      var rank = Array.prototype.indexOf.call(siblings, el);
      var step = rank < 0 ? 0 : rank % 4;

      el.style.setProperty("--sai-reveal-delay", step * 110 + "ms");

      // En mode lié au défilement, la cascade se fait en décalant la
      // plage. Elle porte désormais sur toute la traversée (`cover`) :
      // chaque élément de la rangée démarre un peu plus tard et finit
      // un peu plus tôt que son voisin.
      el.style.setProperty("--sai-r0", step * 5 + "%");
      el.style.setProperty("--sai-r1", 100 - step * 3 + "%");

      if (observer) observer.observe(el);
    }
  }

  /**
   * Entrée du haut de page, jouée une fois à l'ouverture.
   *
   * Une apparition liée au défilement n'a pas de sens ici : la barre de
   * menu est collante et ne quitte jamais l'écran, les bandeaux non
   * plus. Ils s'animent donc à l'arrivée, échelonnés de haut en bas.
   *
   * Le header n'obtient qu'un fondu d'opacité : un `transform` ferait de
   * lui un bloc conteneur pour ses descendants en `position: fixed`, et
   * les méga-menus s'ancreraient de nouveau sur lui — le défaut
   * d'affichage des menus déroulants corrigé plus tôt.
   */
  function enter() {
    var groups = [
      { sel: '.sai-trust-bar', mode: 'slide' },
      { sel: '.announcement-bar, [class*="header-announcements"]', mode: 'slide' },
      { sel: '.header-section .header__columns', mode: 'fade' },
      // Uniquement une vraie bannière. « La première section de main »
      // désignait aussi le bloc produit sur une fiche — or il contient la
      // barre d'ajout au panier en `position: fixed`, qui s'ancrait alors
      // sur la section animée au lieu de la fenêtre.
      { sel: 'main > .shopify-section:first-of-type:has(slideshow-component, [class*="slideshow"], [class*="banner"])', mode: 'hero' }
    ];

    var rank = 0;
    for (var g = 0; g < groups.length; g++) {
      var found;
      try {
        found = document.querySelectorAll(groups[g].sel);
      } catch (e) {
        continue;
      }

      for (var i = 0; i < found.length; i++) {
        var el = found[i];
        if (el.hasAttribute('data-sai-enter')) continue;

        // Le mode `fade` ne pose pas de transform : il reste sûr même
        // au-dessus d'un élément fixe. Les deux autres non.
        if (groups[g].mode !== 'fade' && containsFixed(el)) continue;

        el.setAttribute('data-sai-enter', groups[g].mode);
        el.style.setProperty('--sai-enter-delay', rank * 130 + 'ms');
        rank++;
      }
    }
  }

  root.classList.add('sai-motion');
  if (scrollDriven) root.classList.add('sai-motion-scroll');

  enter();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arm, { once: true });
  } else {
    arm();
  }

  document.addEventListener('shopify:section:load', arm);

  /**
   * Contenu qui arrive après coup.
   *
   * « You may also like » charge ses produits en différé : au moment où
   * ce script s'exécute, la section est vide, et ses cartes n'étaient
   * donc jamais prises en compte. Même chose pour le défilement infini
   * et les filtres de collection. On surveille les ajouts de nœuds et
   * on relance, en groupant les rafales pour ne pas tourner en boucle.
   */
  if ('MutationObserver' in window) {
    var rearmTimer = null;
    var watcher = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        if (records[i].addedNodes && records[i].addedNodes.length) {
          clearTimeout(rearmTimer);
          rearmTimer = setTimeout(arm, 350);
          return;
        }
      }
    });

    watcher.observe(document.body || document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Repart de zéro une fois les images dimensionnées.
   *
   * Au moment du DOM, un carrousel dont les images ne sont pas encore
   * chargées mesure quelques pixels : il n'est donc pas reconnu comme
   * conteneur ample, se fait marquer lui-même, et ses cartes se
   * retrouvent bloquées pour de bon. En remesurant après `load`, les
   * hauteurs sont réelles et la répartition conteneur/enfants est juste.
   */
  window.addEventListener('load', function () {
    var marked = document.querySelectorAll('[data-sai-reveal]');
    for (var i = 0; i < marked.length; i++) {
      if (observer) observer.unobserve(marked[i]);
      marked[i].removeAttribute('data-sai-reveal');
      marked[i].classList.remove('is-revealed');
    }
    arm();
  });

  // Filet de sécurité : rien ne doit rester invisible si le mécanisme
  // de repli ne se déclenche pas (onglet en arrière-plan au chargement).
  if (!scrollDriven) {
    setTimeout(function () {
      var all = document.querySelectorAll('[data-sai-reveal]:not(.is-revealed)');
      for (var i = 0; i < all.length; i++) {
        var box = all[i].getBoundingClientRect();
        if (box.top < window.innerHeight && box.bottom > 0) {
          all[i].classList.add('is-revealed');
        }
      }
    }, 2500);
  }
})();
